import * as React from 'react';
import { styled } from '@mui/material/styles';
import { Translate } from '../../../../../../../shared/LanguageService';
import { AuthenticatedUser } from '../../../../../../../shared/types';
import { useImageProcessingCommentsQuery } from '../queries/use-query-image-processing-comments';
import { useUpdateCommentMutation } from '../queries/use-mutation-update-comment';
import { getVisibleCommentPanels } from '../state/utils';
import { CommentPanel, CommentPanelSkeleton } from './CommentPanel';

const CommentsGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
    [theme.breakpoints.down('sm')]: {
        gridTemplateColumns: '1fr'
    }
}));

type ImageEditorCommentsSectionProps = {
    orderId: number;
    user: AuthenticatedUser;
};

export function ImageEditorCommentsSection({ orderId, user }: ImageEditorCommentsSectionProps) {
    let panels = getVisibleCommentPanels(user);
    let commentsQuery = useImageProcessingCommentsQuery({ orderId, user });
    let updateComment = useUpdateCommentMutation({ orderId, user });

    if (panels.length === 0) {
        return null;
    }

    if (commentsQuery.isLoading || !commentsQuery.data) {
        return <ImageEditorCommentsSectionSkeleton user={user} />;
    }

    let comments = commentsQuery.data;

    return (
        <CommentsGrid>
            {panels.map((panel) => (
                <CommentPanel
                    key={panel.type}
                    title={Translate(panel.titleKey)}
                    text={comments[panel.field]}
                    canEdit={panel.canEdit}
                    isSaving={
                        updateComment.isLoading && updateComment.variables.type === panel.type
                    }
                    onSave={(text) => updateComment.mutateAsync({ text, type: panel.type })}
                />
            ))}
            {comments.editingStyle && (
                <CommentPanel
                    title={Translate('image-processing/page/panel-header/editing-style')}
                    text={comments.editingStyle}
                    canEdit={false}
                    isSaving={false}
                    onSave={() => Promise.resolve()}
                />
            )}
        </CommentsGrid>
    );
}

type ImageEditorCommentsSectionSkeletonProps = {
    user: AuthenticatedUser;
};

export function ImageEditorCommentsSectionSkeleton({
    user
}: ImageEditorCommentsSectionSkeletonProps) {
    let panels = getVisibleCommentPanels(user);

    if (panels.length === 0) {
        return null;
    }

    return (
        <CommentsGrid aria-busy="true">
            {panels.map((panel) => (
                <CommentPanelSkeleton key={panel.type} canEdit={panel.canEdit} />
            ))}
        </CommentsGrid>
    );
}
